// OTA-safe Live Activity state machine types.
//
// IMPORTANT: the shipped iOS LiveActivityBridge only accepts `isTracking: bool`
// on update(). We keep the richer state machine here so the web side has the
// full picture (for debug/log/analytics, and so a future native submission can
// expand `ContentState` without re-wiring all the call sites). Today we map
// down to `isTracking` via `mapToBridgeIsTracking()`.

export type TrackingPhase = "off" | "active" | "paused";
export type UploadPhase = "idle" | "uploading" | "paused" | "complete";

export type LAState =
  | "ended"
  | "tracking_active"
  | "tracking_paused"
  | "uploading"
  | "upload_paused"
  | "tracking_and_uploading"
  | "upload_complete";

export interface LAInput {
  tracking: TrackingPhase;
  upload: UploadPhase;
}

export interface LASnapshot {
  state: LAState;
  isTracking: boolean;
  /** True if any visible activity should be shown (start/keep alive). */
  active: boolean;
}

export function deriveLAState({ tracking, upload }: LAInput): LAState {
  if (tracking === "active" && upload === "uploading") return "tracking_and_uploading";
  if (tracking === "active" && upload === "paused") return "tracking_active"; // tracking dominates
  if (tracking === "active") return "tracking_active";
  if (tracking === "paused" && upload === "uploading") return "uploading";
  if (tracking === "paused") return "tracking_paused";
  // tracking === "off" — uploads alone never keep the Live Activity alive.
  // The pill ends the instant tracking stops; UploadWorker drains silently.
  return "ended";
}

/** Map the rich state down to the shipped bridge's single `isTracking` boolean. */
export function mapToBridgeIsTracking(state: LAState): boolean {
  switch (state) {
    case "tracking_active":
    case "tracking_and_uploading":
    case "tracking_paused":
    case "uploading":
      // Pause is still "tracking" from ActivityKit's perspective — the widget
      // distinguishes via isPaused. Mapping pause to false would hide the
      // Resume button and the cadence switcher on the Lock Screen / Island.
      return true;
    case "upload_paused":
    case "upload_complete":
    case "ended":
      return false;
  }
}


export function snapshot(input: LAInput): LASnapshot {
  const state = deriveLAState(input);
  return {
    state,
    isTracking: mapToBridgeIsTracking(state),
    active: state !== "ended",
  };
}
